import { Fragment, FC, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { Link } from 'react-router';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ListState, { hasListState } from '../../components/ListState';
import { VISUALLY_HIDDEN_SX } from '../../components/ListTable';
import { colors, fontWeights, radii } from '../../theme/tokens';
import { DocumentAction } from './RowActions';
import {
  DOCUMENTS_ERROR_TEXT,
  RowResolvers,
  emptyDocumentsText,
} from './DocumentsTable';
import { HomeDocument } from './types';

// The mobile Résultats table of the smartphone XD (S8 in DESIGN.md): two
// columns — "Nom du fichier" and a sortable "Service" — plus an orange "•••"
// per row that expands a grey band right under it with the four actions
// (view, edit, download, delete). There is no hover on touch: the band is
// the only way to the actions, and one row is open at a time.
//
// The Service sort is client-side over the page in hand: the listing API
// has no sort parameter yet (noted for LTU). Tapping the header cycles
// ascending → descending → none.
//
// Shared by Résultats (its own screen, S8) and the Home on mobile: LTU asked
// to keep a document list on the mobile Home instead of the XD's tile-only
// hub, styled like Résultats (see pages/Home/index.tsx). Home is the base
// screen other listings build on (DocumentsTable, DocumentGrid, RowActions…
// all live here too), so this file follows that convention rather than
// Résultats importing back from it.

type Props = {
  documents: HomeDocument[];
  isLoading: boolean;
  isError: boolean;
  errorText?: string;
  /** Whether a filter/search/criterion narrows the list, for the empty text. */
  hasFilters?: boolean;
  resolvers: RowResolvers;
  onAction: (action: DocumentAction, document: HomeDocument) => void;
  onRetry: () => void;
};

type Sort = 'asc' | 'desc' | null;

const ACTION_ICON_SX = { color: colors.accent, p: 0.5 } as const;

const MobileResultsTable: FC<Props> = ({
  documents,
  isLoading,
  isError,
  errorText = DOCUMENTS_ERROR_TEXT,
  hasFilters = true,
  resolvers,
  onAction,
  onRetry,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>(null);

  const state = {
    isLoading,
    isError,
    isEmpty: documents.length === 0,
    errorText,
    emptyText: emptyDocumentsText(hasFilters),
    onRetry,
  };

  const sorted = useMemo(() => {
    if (!sort) return documents;
    const direction = sort === 'asc' ? 1 : -1;
    return [...documents].sort(
      (a, b) =>
        direction *
        resolvers.serviceName(a).localeCompare(resolvers.serviceName(b), 'fr'),
    );
  }, [documents, sort, resolvers]);

  const cycleSort = () =>
    setSort((current) =>
      current === null ? 'asc' : current === 'asc' ? 'desc' : null,
    );

  const run = (action: DocumentAction, document: HomeDocument) => {
    setExpandedId(null);
    onAction(action, document);
  };

  return (
    <TableContainer
      sx={{ borderRadius: `${radii.card}px` }}
    >
      <Table aria-label="Documents" data-testid="mobile-results-table">
        <TableHead>
          <TableRow>
            <TableCell>Nom du fichier</TableCell>
            <TableCell
              aria-sort={
                sort === 'asc'
                  ? 'ascending'
                  : sort === 'desc'
                  ? 'descending'
                  : undefined
              }
              sx={{ width: '35%' }}
            >
              <Box
                component="button"
                type="button"
                onClick={cycleSort}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                Service
                <ArrowDropDownIcon
                  fontSize="small"
                  sx={{
                    opacity: sort ? 1 : 0.7,
                    transform: sort === 'desc' ? 'rotate(180deg)' : 'none',
                  }}
                />
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ width: 48 }}>
              <Box component="span" sx={VISUALLY_HIDDEN_SX}>
                Actions
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            '& .MuiTableCell-body': {
              // XD: no outer border; the rule between rows is orange (the
              // mobile artboard draws it grey, kept orange like desktop).
              borderBottom: `1px solid ${colors.accent}`,
              color: colors.textBase,
              py: 1,
            },
          }}
        >
          {hasListState(state) ? (
            <TableRow>
              <TableCell colSpan={3} sx={{ border: 0 }}>
                <ListState {...state} />
              </TableCell>
            </TableRow>
          ) : (
            sorted.map((document) => {
              const expanded = expandedId === document.id;
              const canWrite = resolvers.canWrite(document);

              return (
                <Fragment key={document.id}>
                  <TableRow sx={{ backgroundColor: colors.white }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Link
                          to={`/document/${document.id}`}
                          style={{
                            color: colors.textBase,
                            fontWeight: fontWeights.medium,
                            textDecoration: 'none',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {document.title}
                        </Link>
                        {document.confidential && (
                          <Tooltip title="Confidentiel">
                            <LockOutlinedIcon
                              fontSize="small"
                              sx={{ color: colors.muted }}
                              aria-label="Confidentiel"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{resolvers.serviceName(document)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={`Actions pour ${document.title}`}
                        aria-expanded={expanded}
                        onClick={() =>
                          setExpandedId(expanded ? null : document.id)
                        }
                        sx={{ color: colors.accent, p: 0.5 }}
                      >
                        <MoreHorizIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {expanded && (
                    <TableRow data-testid="mobile-actions-band">
                      <TableCell
                        colSpan={3}
                        sx={{ backgroundColor: colors.border, py: 0.25 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Tooltip title="Voir">
                            <IconButton
                              aria-label={`Voir ${document.title}`}
                              onClick={() => run('view', document)}
                              sx={ACTION_ICON_SX}
                            >
                              <VisibilityOutlinedIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                          {canWrite && (
                            <Tooltip title="Modifier">
                              <IconButton
                                aria-label={`Modifier ${document.title}`}
                                onClick={() => run('edit', document)}
                                sx={ACTION_ICON_SX}
                              >
                                <EditOutlinedIcon fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Télécharger">
                            <IconButton
                              aria-label={`Télécharger ${document.title}`}
                              onClick={() => run('download', document)}
                              sx={ACTION_ICON_SX}
                            >
                              <FileDownloadOutlinedIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                          {canWrite && (
                            <Tooltip title="Supprimer">
                              <IconButton
                                aria-label={`Supprimer ${document.title}`}
                                onClick={() => run('delete', document)}
                                sx={ACTION_ICON_SX}
                              >
                                <DeleteOutlinedIcon fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MobileResultsTable;
